/*
===============================================================================================================
                                 Copyright 2025-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/**
   * Transforms an array of country codes into an array of country objects with labels and enabled status.
   * The resulting array is sorted alphabetically by country label.
   *
   * @param {Object.<string, string[]>} [supportedCountryCodesInConfig] - List of supported country codes from the config
   * @param {Object.<string, boolean>} enabledCountriesInCompany - Countries enabled in the merchant's company settings
   * @param {Object.<string, string>} countriesData - Mapping of country codes to country names
   * @returns {Array.<{code: string, label: string, isEnabled: boolean}>} List of country objects sorted by label
   *
   * @example
   * const supportedCodes = ['US', 'CA'];
   * const enabledCountries = { 'US': true, 'CA': false };
   * const countriesList = getCountries(supportedCodes, enabledCountries);
   * // Returns:
   * // [
   * //  { code: 'CA', label: 'Canada', isEnabled: false },
   * //  { code: 'US', label: 'United States', isEnabled: true}
   * // ]
   */
export const getCountries = ({
  supportedCountryCodesInConfig = [],
  enabledCountriesInCompany,
  countriesData,
} = {}) => supportedCountryCodesInConfig
  .map((countryCode) => ({
    code: countryCode,
    label: countriesData[countryCode],
    isEnabled: enabledCountriesInCompany[countryCode] || false,
  }))
  .sort((countryA, countryB) => countryA.label?.localeCompare(countryB.label));

/**
   * Converts a carrier's enabled countries object into a sorted array of country objects.
   *
   * @param {Object.<string, boolean>} [carrierCountriesEnabled={}] - An object mapping country codes to their enabled status
   * @param {Object.<string, string>} countriesData - Mapping of country codes to country names
   * @returns {Array.<{code: string, label: string, isEnabled: boolean}>} List of enabled countries sorted alphabetically by label
   *
   * @example
   * const carrierCountries = { 'US': true, 'CA': true, 'MX': false };
   * const supported = getCarrierSupportedCountries(carrierCountries);
   * // Returns: [{ code: 'CA', label: 'Canada', isEnabled: true }, { code: 'US', label: 'United States', isEnabled: true }]
   */
export const getCarrierSupportedCountries = (carrierCountriesEnabled = {}, countriesData) => {
  const supportedCountries = [];
  Object.entries(carrierCountriesEnabled).forEach(([countryCode, isEnabled]) => {
    if (isEnabled) {
      supportedCountries.push({
        code: countryCode,
        label: countriesData[countryCode],
        isEnabled,
      });
    }
  });
  return supportedCountries.sort((countryA, countryB) => countryA.label?.localeCompare(countryB.label));
};
